import {
  ActionIcon,
  AppShell,
  Box,
  Burger,
  Button,
  Group,
  Loader,
  Menu,
  NavLink,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconBell,
  IconDownload,
  IconKey,
  IconLanguage,
  IconLogout,
  IconPackage,
  IconRefresh,
  IconUser,
} from '@tabler/icons-react';
import { APP_FOOTER_HEIGHT, AppFooter } from './AppFooter';
import { activeDestinationId, activeTrail, NAV_TREE, visibleNav } from './navigation';
import type { NavNode } from './navigation';
import { useAuth } from '../features/auth/useAuth';
import { usePermissions } from '../features/auth/usePermissions';
import { useTenant } from '../features/tenant/useTenant';
import { useInstallPrompt } from '../shared/hooks/usePwaInstall';
import { formatAppVersion } from '../shared/version';
import { checkForUpdate } from '../shared/versionCheck';
import { RecoveryCodesModal } from '../features/auth/RecoveryCodesModal';
import { WarehouseGate } from '../features/warehouses/WarehouseGate';
import { WarehouseSwitcher } from '../features/warehouses/WarehouseSwitcher';
import { getRuntimeConfig } from '../shared/config/runtime';
import { LanguageModal } from '../shared/i18n/LanguagePicker';
import { localeOption } from '../shared/i18n/localeOptions';
import { useLocaleStore } from '../shared/i18n/localeStore';
import { useLocalePreferenceSync } from '../shared/i18n/useLocalePreferenceSync';

/**
 * Authenticated application shell. Built for "App Mode" — the installed,
 * standalone PWA window that has no browser address bar or back button — so it
 * carries all its own navigation: a burger-toggled left nav, a brand mark that
 * returns home, an account menu with sign-out, and (in a browser tab only) an
 * in-app Install button. Per-page Back/actions live in PageHeader.
 */
/**
 * iOS SAFE AREAS, AND WHY THE HEADER CARRIES THEM
 *
 * index.html asks for `viewport-fit=cover` and `black-translucent`, which is what lets the navy
 * header reach the very top of the screen instead of leaving a white band under the status bar.
 * The cost is that the page is drawn UNDER the status bar and the Dynamic Island, and Mantine's
 * AppShell only compensates at the bottom (its footer CSS adds `env(safe-area-inset-bottom)`) —
 * nothing at the top.
 *
 * So on an iPhone Pro Max in portrait the whole 56px header sat inside the ~59px top inset: the
 * burger was painted under the Dynamic Island, and iOS ate every tap on it. Rotating to landscape
 * moved the inset to the side and the menu started working, which is what made it look like a
 * width problem. It is not — Android reports no top inset for a punch-hole, which is why it never
 * reproduced on an S25 Ultra.
 *
 * The inset goes on the header's HEIGHT rather than on its padding alone, because that variable is
 * what the navbar's `top`, the navbar's height and Main's top padding are all derived from; padding
 * by itself would clear the burger and leave everything below it overlapping. An equal padding then
 * pushes the header's own row clear of the island.
 *
 * The horizontal insets matter in landscape, where the island sits beside the content instead of
 * above it — without them the first nav row and the leading table column tuck underneath it.
 */
const HEADER_CONTENT_HEIGHT = 56;

/**
 * Mantine's Collapse transition is 200ms; this is that plus a frame's grace. Long enough that the
 * expanding group has reached its final height before the nav scrolls to the active row, short
 * enough that the scroll still lands within the drawer's own slide-in and reads as one movement.
 */
const COLLAPSE_SETTLE_MS = 220;

/** `rem()` passes a calc() through untouched, so the scale factor has to be applied by hand. */
const HEADER_HEIGHT =
  `calc(${HEADER_CONTENT_HEIGHT / 16}rem * var(--mantine-scale) + env(safe-area-inset-top))`;

/**
 * Wrapped in calc() on purpose. A bare `env()` is valid CSS and every real browser takes it, but a
 * strict parser is free to discard a declaration it cannot resolve — jsdom does exactly that, which
 * would let this whole fix be deleted with no test noticing. Inside calc() the value survives.
 */
const SAFE_AREA_X = {
  paddingLeft: 'calc(env(safe-area-inset-left))',
  paddingRight: 'calc(env(safe-area-inset-right))',
} as const;

export function AppFrame() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [navOpen, { toggle: toggleNav, close: closeNav }] = useDisclosure(false);
  const [languageOpen, { open: openLanguage, close: closeLanguage }] = useDisclosure(false);
  const [recoveryOpen, { open: openRecovery, close: closeRecovery }] = useDisclosure(false);
  const { user, signOut } = useAuth();
  const permissions = usePermissions();
  const tenant = useTenant();

  // Where we are, and the groups above it. Both read from the whole tree rather than the filtered
  // one, so neither depends on the permission list having arrived yet; an id that is not on screen
  // simply goes unused.
  const activeId = activeDestinationId(NAV_TREE, pathname);
  const trail = activeTrail(NAV_TREE, pathname);
  const [openedGroups, setOpenedGroups] = useState<string[]>(trail);
  const navViewportRef = useRef<HTMLDivElement>(null);

  /**
   * Bring the current screen's row into view when the drawer opens.
   *
   * Making the menu scrollable is only half of it. Nothing ever collapses a group — openOrCloseNav
   * below unions the trail into whatever was already open — so after visiting a few sections every
   * session ends up fully expanded, and on a short window that puts the row you are standing on
   * below the fold. You would open the menu to move somewhere and be shown the middle of it.
   *
   * Scrolls the VIEWPORT by hand rather than calling scrollIntoView, which walks up and scrolls
   * every ancestor it can — including the page behind the drawer.
   */
  useEffect(() => {
    if (!navOpen) {
      return;
    }

    // The group holding the active row may only be expanding now, and Mantine's Collapse animates
    // its height (200ms). Measuring before that settles scrolls to where the row used to be.
    const timer = window.setTimeout(() => {
      const viewport = navViewportRef.current;
      const active = viewport?.querySelector('[data-active], [aria-current="page"]');
      if (!viewport || !active) {
        return;
      }

      const view = viewport.getBoundingClientRect();
      const row = active.getBoundingClientRect();
      const margin = 8;

      if (row.top < view.top + margin) {
        viewport.scrollTop -= view.top + margin - row.top;
      } else if (row.bottom > view.bottom - margin) {
        viewport.scrollTop += row.bottom - view.bottom + margin;
      }
    }, COLLAPSE_SETTLE_MS);

    return () => window.clearTimeout(timer);
  }, [navOpen, activeId, openedGroups]);

  /**
   * The menu is retrieved when it is opened, not held from earlier in the session — matching
   * how the other products in this family behave, and for the same reason the API re-checks on
   * every request: a permission granted or revoked a minute ago has to be reflected now. A menu
   * built once at sign-in would keep offering a section the user has since lost.
   */
  function openOrCloseNav() {
    if (!navOpen) {
      void permissions.refetch();
      // Expand what holds the current screen. The drawer is opened to go somewhere, and the row
      // you are on is the landmark for finding the next one. Anything the user opened by hand
      // stays open.
      setOpenedGroups((open) => Array.from(new Set([...open, ...trail])));
    }
    toggleNav();
  }

  // Unfiltered when the permission list could not be loaded. Filtering against an empty set
  // would hide every section from a user who has all of them — an API hiccup, or a tenant
  // database missing the migration that added the permissions procedure, would look to the user
  // like their app had lost most of its features. The server still refuses anything they
  // genuinely lack, so an unfiltered menu costs at most a 403 on a section they cannot read.
  const visibleNavItems = permissions.isUnknown
    ? NAV_TREE
    : visibleNav(NAV_TREE, permissions.has);

  function renderNavNode(node: NavNode) {
    const Icon = node.icon;

    if (node.children) {
      const opened = openedGroups.includes(node.id);
      return (
        <NavLink
          key={node.id}
          label={t(node.labelKey)}
          leftSection={<Icon size={18} />}
          childrenOffset={28}
          color="invetrak"
          variant="light"
          // Only while it is closed: an open group already shows the active row inside it, and
          // highlighting both reads as two places at once.
          active={!opened && trail.includes(node.id)}
          opened={opened}
          onChange={(next) =>
            setOpenedGroups((open) =>
              next ? [...open, node.id] : open.filter((id) => id !== node.id),
            )
          }
        >
          {node.children.map((child) => renderNavNode(child))}
        </NavLink>
      );
    }

    return (
      <NavLink
        key={node.id}
        component={Link}
        to={node.to}
        // Exact, or TanStack marks this link aria-current="page" for anything BENEATH it as well —
        // its default match is a prefix. Mantine styles `:where([data-active], [aria-current='page'])`,
        // so on /app/inventory/adjust the Inventory row lit up next to Adjustment: two rows claiming
        // to be the current page, and two highlights. Which row is active is decided by activeId
        // below, using the longest match, and that is the only thing that should decide it.
        activeOptions={{ exact: true }}
        label={t(node.labelKey)}
        leftSection={<Icon size={18} />}
        active={node.id === activeId}
        color="invetrak"
        variant="light"
        // Destinations only. A group's onClick runs before Mantine toggles it, so closing the
        // drawer here would shut it in the same gesture that expanded the group.
        onClick={closeNav}
      />
    );
  }
  const { canInstall, promptInstall } = useInstallPrompt();
  const { locale } = useLocaleStore();
  const currentLocale = localeOption(locale);

  // The language choice lives on the account, not just in this browser.
  useLocalePreferenceSync();

  const navy = 'var(--mantine-color-invetrak-8)';

  return (
    <AppShell
      header={{ height: HEADER_HEIGHT }}
      navbar={{ width: 250, breakpoint: 'sm', collapsed: { mobile: !navOpen, desktop: !navOpen } }}
      footer={{ height: APP_FOOTER_HEIGHT }}
      padding="md"
    >
      <AppShell.Header
        style={{
          background: navy,
          borderBottom: 0,
          color: '#fff',
          // Pushes the row below the status bar. The box grew by the same amount above, so the
          // inner Group's h="100%" still resolves to the 56px content height.
          paddingTop: 'calc(env(safe-area-inset-top))',
          ...SAFE_AREA_X,
        }}
      >
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger
              opened={navOpen}
              onClick={openOrCloseNav}
              size="sm"
              color="#fff"
              aria-label={t('shell.toggleNav')}
              // Mantine's Burger animates but says nothing: without this a screen reader hears
              // "Toggle navigation, button" whether the drawer is open or shut.
              aria-expanded={navOpen}
            />
            <UnstyledButton onClick={() => navigate({ to: '/app' })} aria-label={t('nav.home')}>
              <Group gap={8} wrap="nowrap">
                <Box
                  w={30}
                  h={30}
                  style={{
                    borderRadius: 8,
                    background: 'rgba(255,255,255,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconPackage size={18} color="#fff" />
                </Box>
                <Text c="#fff" fw={700} size="lg" visibleFrom="xs">
                  {t('app.name')}
                </Text>
              </Group>
            </UnstyledButton>
          </Group>

          <Group gap="xs" wrap="nowrap">
            {canInstall && (
              <Button
                size="xs"
                variant="white"
                color="invetrak"
                leftSection={<IconDownload size={14} />}
                onClick={promptInstall}
                visibleFrom="xs"
              >
                {t('shell.install')}
              </Button>
            )}
            {/* Which warehouse everything on screen is about. Legacy shows it in the same
                place, and permissions now depend on it, so it is not decoration. */}
            <WarehouseSwitcher />
            <ActionIcon variant="subtle" color="gray" c="#fff" aria-label={t('shell.notifications')}>
              <IconBell size={18} />
            </ActionIcon>
            <Stack gap={0} align="flex-end" visibleFrom="sm">
              <Text c="#fff" size="sm" fw={600} lh={1.15}>
                {t('shell.welcome', { name: user?.displayName ?? '' })}
              </Text>
              {tenant.data && (
                <Text c="rgba(255,255,255,0.75)" size="xs" lh={1.15}>
                  {tenant.data.displayName}
                </Text>
              )}
            </Stack>
            {/* Wide enough for the four language pills on one row: they measure
                ~226px (한국어 is the widest at 66px), and 280 leaves ~26px of
                slack for CJK font fallbacks on other machines. They wrap
                harmlessly if a font is wider still. */}
            <Menu position="bottom-end" width={280} withArrow>
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" c="#fff" aria-label={t('shell.account')}>
                  <IconUser size={18} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{user?.email}</Menu.Label>
                {canInstall && (
                  <Menu.Item
                    hiddenFrom="xs"
                    leftSection={<IconDownload size={16} />}
                    onClick={promptInstall}
                  >
                    {t('shell.install')}
                  </Menu.Item>
                )}
                <Menu.Item
                  leftSection={<IconRefresh size={16} />}
                  onClick={() => void checkForUpdate(true)}
                >
                  {t('shell.checkForUpdates')}
                </Menu.Item>
                {/* Here rather than on a settings screen there is no room for yet: this menu is the
                    only place reachable from every screen in App Mode, and somebody who needs
                    recovery codes needs them from wherever they happen to be. */}
                <Menu.Item
                  leftSection={<IconKey size={16} />}
                  onClick={openRecovery}
                >
                  {t('auth.recovery.title')}
                </Menu.Item>
                <Menu.Divider />
                {/* Language lives here because it's a per-user preference, and
                    this menu is the only place reachable from every screen in
                    App Mode (no browser chrome to fall back on). It opens the
                    shared picker, which scales past the four current languages —
                    the modal is rendered outside <Menu> below, since anything
                    inside the dropdown unmounts when the menu closes. */}
                <Menu.Item
                  leftSection={<IconLanguage size={16} />}
                  onClick={openLanguage}
                >
                  <Group justify="space-between" gap="xs" wrap="nowrap">
                    <span>{t('languageSwitcher.label')}</span>
                    <Text size="xs" c="dimmed">
                      {currentLocale?.native ?? locale}
                    </Text>
                  </Group>
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size={16} />}
                  onClick={signOut}
                >
                  {t('auth.signOut')}
                </Menu.Item>
                <Menu.Divider />
                <Text size="xs" c="dimmed" ta="center" py={4}>
                  {formatAppVersion(getRuntimeConfig().environment)}
                </Text>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      {/* Outside <Menu> on purpose: the dropdown unmounts its children when it
          closes, which would take the modal with it. */}
      <LanguageModal opened={languageOpen} onClose={closeLanguage} />
      <RecoveryCodesModal opened={recoveryOpen} onClose={closeRecovery} />

      {/* Only the leading edge: in landscape the island is beside the drawer, not above it. */}
      {/* Only the leading edge padding is ours; the SECTION is what makes the menu reachable.
          AppShell.Navbar is position:fixed with its height clamped between the header and the
          footer, and Mantine sets no overflow on it at all — so before this, a menu taller than
          the window simply ran off the bottom. Not clipped, UNREACHABLE: a fixed element's
          overflow does not extend the document's scroll height, so no scrollbar appeared
          anywhere and no gesture could reveal it.

          Fully expanded the menu is 21 rows at 37.7px, which with gaps and padding needs a
          916px-tall window. Anything shorter lost rows off the bottom, and a phone in landscape
          could not reach the end of Setup on its own. It is worse the MORE permissions you hold,
          because visibleNav only ever removes rows — so a TenantAdmin got the tallest menu and
          an operator never saw the bug.

          `component={ScrollArea}` is load-bearing and NOT a tidy-up target. It makes the section
          BE the ScrollArea root, which carries `overflow: hidden` — and a flex item that is a
          scroll container has an automatic minimum size of zero, so it may shrink below its
          content. Rewrite it as `<AppShell.Section grow><ScrollArea>` and the section becomes a
          plain div whose `min-height: auto` resolves to its content height: it grows to 791px
          inside a 560px navbar and nothing scrolls at all. Measured both ways against the real
          stylesheet — the wrapper form reproduces the original bug exactly. */}
      <AppShell.Navbar
        p="sm"
        style={{ paddingInlineStart: 'calc(var(--mantine-spacing-sm) + env(safe-area-inset-left))' }}
      >
        <AppShell.Section grow component={ScrollArea} type="auto" viewportRef={navViewportRef}>
          <Stack gap={4}>
            {visibleNavItems.map((node) => renderNavNode(node))}
            {/* Only while the first fetch is in flight. Home is unconditional, so the menu is
                never empty — this sits under it rather than replacing the whole panel, which
                would make opening the menu feel like it had failed. */}
            {permissions.isLoading && (
              <Group justify="center" py="xs">
                <Loader size="xs" />
              </Group>
            )}
          </Stack>
        </AppShell.Section>
      </AppShell.Navbar>

      {/* Mantine's own formula, plus the insets. Restating it keeps the navbar and aside offsets
          that a bare paddingLeft would clobber on desktop. */}
      <AppShell.Main
        style={{
          paddingInlineStart:
            'calc(var(--app-shell-navbar-offset, 0rem) + var(--app-shell-padding) + env(safe-area-inset-left))',
          paddingInlineEnd:
            'calc(var(--app-shell-aside-offset, 0rem) + var(--app-shell-padding) + env(safe-area-inset-right))',
        }}
      >
        <WarehouseGate>
          <Outlet />
        </WarehouseGate>
      </AppShell.Main>

      {/* Mantine already reserves the bottom inset here; only the sides are ours. */}
      <AppShell.Footer style={{ background: navy, borderTop: 0, ...SAFE_AREA_X }}>
        <AppFooter variant="solid" />
      </AppShell.Footer>
    </AppShell>
  );
}
